/**
 * User Type Permission utility for user-type-based access control
 * Handles permissions for MpVap, PlusCard, and All user types
 */

import {
  PLUS_CARD,
  PARTNER,
  PARTNER_VIEW_CUSTOMERS,
  PARTNER_IMPORT_CUSTOMERS,
  PARTNER_IMPORT_LOGS,
  MDM,
  LANDING,
  ADMIN,
} from "../routes/paths";

/**
 * Check if user has access to MpVap features
 * MpVap: Full access to all pages except PlusCard
 * All: Full access to everything
 * PlusCard: No access to MpVap-specific features
 */
export const canAccessMpVapFeatures = (userTypeName: string): boolean => {
  return userTypeName === "MpVap" || userTypeName === "All";
};

/**
 * Check if user has access to PlusCard features
 * PlusCard: Full access to PlusCard pages
 * All: Full access to everything
 * MpVap: No access to PlusCard features
 */
export const canAccessPlusCardFeatures = (userTypeName: string): boolean => {
  return userTypeName === "PlusCard" || userTypeName === "All";
};

/**
 * Check if user should see the Home/Landing page
 * MpVap: Yes
 * All: Yes
 * PlusCard: No (should be redirected to /plus-card)
 */
export const canAccessLandingPage = (userTypeName: string): boolean => {
  return userTypeName === "MpVap" || userTypeName === "All";
};

/**
 * Check if user has full access to master data features
 * MpVap: Full access (based on role permissions)
 * All: Full access (based on role permissions)
 * PlusCard: Read-only access to Business Partners and Sites
 */
export const canAccessMasterDataFull = (userTypeName: string): boolean => {
  return userTypeName === "MpVap" || userTypeName === "All";
};

/**
 * Check if user has read-only access to Business Partners
 * PlusCard: Read-only access
 * Others: Based on role permissions
 */
export const isPlusCardReadOnlyMode = (userTypeName: string): boolean => {
  return userTypeName === "PlusCard";
};

/**
 * Get the default redirect path based on user type
 * PlusCard: /plus-card
 * Others: /landing
 */
export const getDefaultRedirectPath = (userTypeName: string): string => {
  if (userTypeName === "PlusCard") {
    return PLUS_CARD;
  }
  return LANDING;
};

/**
 * Check if user can access a specific path based on user type
 * @param userTypeName - The user type name
 * @param path - The path to check
 * @returns boolean indicating if the user can access the path
 */
export const canAccessPath = (userTypeName: string, path: string): boolean => {
  // All user type has access to everything
  if (userTypeName === "All") {
    return true;
  }

  // MpVap user type restrictions
  if (userTypeName === "MpVap") {
    // Cannot access PlusCard page
    if (path === PLUS_CARD || path.startsWith(PLUS_CARD + "/")) {
      return false;
    }
    // Can access everything else
    return true;
  }

  // PlusCard user type restrictions
  if (userTypeName === "PlusCard") {
    // Can access PlusCard page and any subroutes
    if (path === PLUS_CARD || path.startsWith(PLUS_CARD + "/")) {
      return true;
    }

    // Can only access Business Partners view (not import tabs)
    if (path === PARTNER_VIEW_CUSTOMERS) {
      return true;
    }

    // Block import-customers and import-logs for PlusCard users
    if (
      path === PARTNER_IMPORT_CUSTOMERS ||
      path === PARTNER_IMPORT_LOGS ||
      path.startsWith(PARTNER + "/import")
    ) {
      return false;
    }

    // Allow parent /partner route (will redirect to view-customers)
    if (path === PARTNER) {
      return true;
    }

    // Can access Site Master (view only)
    if (path === MDM || path.startsWith(MDM + "/")) {
      return true;
    }

    // Can access User Management (/admin)
    if (path === ADMIN || path.startsWith(ADMIN + "/")) {
      return true;
    }

    // Cannot access any other pages including landing
    return false;
  }

  // Default: no access
  return false;
};

/**
 * Get list of accessible menu keys based on user type
 * Used to filter navigation menu items
 */
export const getAccessibleMenuKeys = (userTypeName: string): string[] => {
  // All user type has access to everything
  if (userTypeName === "All") {
    return ["1-0", "1-1", "1-2", "1-3", "1-4"]; // All menu items
  }

  // MpVap user type: Everything except PlusCard
  if (userTypeName === "MpVap") {
    return ["1-0", "1-1", "1-2", "1-4"]; // Home, MasterData, Pricing, Reports
  }

  // PlusCard user type: Only PlusCard and limited MasterData
  if (userTypeName === "PlusCard") {
    return ["1-1", "1-3"]; // MasterData (limited), PlusCard
  }

  // Default: no access
  return [];
};

/**
 * Get accessible master data sub-menu keys based on user type
 * Used to filter master data sub-menu items for PlusCard users
 */
export const getAccessibleMasterDataKeys = (userTypeName: string): string[] => {
  // All and MpVap users have access to all master data items
  if (userTypeName === "All" || userTypeName === "MpVap") {
    return ["1-1-1", "1-1-2", "1-1-3", "1-1-4", "1-1-5"];
  }

  // PlusCard users only have access to Business Partners and Sites
  if (userTypeName === "PlusCard") {
    return ["1-1-1", "1-1-3"]; // Site Master, Business Partners
  }

  return [];
};
